/**
 * ALTIORA · Keyboard shortcuts hook.
 *
 * Registers a keyboard shortcut on the document with sensible defaults :
 *   · Automatically ignores when the user is typing inside inputs / textareas / contentEditable
 *   · Respects meta/ctrl/shift/alt modifiers
 *   · Cleans up on unmount
 *
 *   useKeyboardShortcut("k", () => openCommandMenu(), { meta: true });
 *   useKeyboardShortcut("Escape", () => onClose());
 *   useKeyboardShortcut("?", () => setHelpOpen(true), { shift: true });
 *
 * For a11y — always mirror the shortcut in a visible UI (Tooltip, kbd tag)
 * so screen-reader / motor-impaired users are not disadvantaged.
 */
import { useEffect } from "react";

const isEditableTarget = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return el.isContentEditable;
};

export function useKeyboardShortcut(
  key,
  handler,
  { meta = false, ctrl = false, shift = false, alt = false, allowInInput = false, enabled = true } = {}
) {
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if (!allowInInput && isEditableTarget(e.target)) return;
      const k = key.length === 1 ? key.toLowerCase() : key;
      const ek = e.key.length === 1 ? e.key.toLowerCase() : e.key;
      if (ek !== k) return;
      if (meta  && !(e.metaKey || e.ctrlKey)) return;
      if (ctrl  && !e.ctrlKey)  return;
      if (shift && !e.shiftKey) return;
      if (alt   && !e.altKey)   return;
      // Prevent browser defaults for `⌘+K`, `⌘+/`, etc.
      if (meta || ctrl) e.preventDefault();
      handler(e);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [key, handler, meta, ctrl, shift, alt, allowInInput, enabled]);
}

/** Small helper to render a keyboard hint (`<Kbd>⌘ K</Kbd>`) inline. */
export function Kbd({ children, testid }) {
  return (
    <kbd
      data-testid={testid}
      className="inline-flex items-center justify-center min-w-[20px] h-5 px-1
                    rounded text-[10px] font-mono font-semibold"
      style={{
        background: "#F1F5F9",
        color:      "#0B2545",
        border:     "1px solid #C5A88055",
        boxShadow:  "inset 0 -1px 0 #C5A88033",
      }}>
      {children}
    </kbd>
  );
}
